// Typed fetch wrappers for all REST API endpoints.

// ─── Base ─────────────────────────────────────────────────────────────────────

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const text = await res.text();
    let message = text;
    try {
      message = JSON.parse(text).error ?? text;
    } catch {
      /* use raw text */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

const get = <T>(url: string) => request<T>(url);
const post = <T>(url: string, body: unknown) =>
  request<T>(url, { method: "POST", body: JSON.stringify(body) });
const put = <T>(url: string, body: unknown) =>
  request<T>(url, { method: "PUT", body: JSON.stringify(body) });
const patch = <T>(url: string, body: unknown) =>
  request<T>(url, { method: "PATCH", body: JSON.stringify(body) });
const del = <T>(url: string) => request<T>(url, { method: "DELETE" });

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StepComponentRef {
  recipe_id: string;
  recipe_name: string;
  recipe_type: string;
  servings_needed: number;
}

export interface ComponentRecipeRef {
  recipe_id: string;
  total_servings_needed: number;
}

export interface Recipe {
  id: string;
  name: string;
  type: "main" | "side" | "base" | "dessert" | null;
  servings: number | null;
  yield_amount: number | null;
  yield_unit: string | null;
  frozen_shelf_life_days: number | null;
  fridge_shelf_life_days: number | null;
  has_meal_prep_steps: number;
  step_count?: number;
  total_servings_remaining?: number | null;
  available_servings?: number | null;
  batch_count?: number | null;
  ingredients_preview?: string | null;
  components_preview?: string | null;
  /** Aggregated component recipe references (from step_components) */
  component_recipes?: ComponentRecipeRef[];
}

export interface StepIngredient {
  step_id: string;
  ingredient_id: string;
  ingredient_name: string;
  ingredient_type: string;
  quantity: string | null;
  unit: string | null;
  optional: boolean;
}

export interface StepResource {
  step_id: string;
  resource_id: string;
  resource_name: string;
  resource_type: string;
  quantity: number;
  temperature_celsius: number | null;
  notes: string | null;
}

export interface Step {
  id: string;
  recipe_id: string;
  description: string;
  phase: "meal-prep" | "day-of-eating" | null;
  order_num: number | null;
  duration_minutes: number | null;
  /** Unattended / low-attention (marinate, bake without watching, rest, etc.) */
  is_passive: boolean;
  variant_id: string | null;
  ingredients: StepIngredient[];
  resources: StepResource[];
  components: StepComponentRef[];
  depends_on: string[];
}

export interface Variant {
  id: string;
  recipe_id: string;
  name: string;
}

export interface BatchStock {
  batch_id: string;
  recipe_id: string;
  recipe_name: string;
  recipe_type: string;
  session_id: string;
  prep_date: string;
  servings_produced: number;
  servings_consumed: number;
  servings_remaining: number;
  storage_location: "fridge" | "freezer";
  variant_id: string | null;
  variant_name: string | null;
  fridge_expiry: string | null;
  freezer_expiry: string | null;
  expiry_date: string | null;
}

export interface RecipeDetail extends Recipe {
  steps: Step[];
  variants?: Variant[];
  batches: BatchStock[];
  available_servings?: number;
}

export interface RecipeStock {
  recipe_id: string;
  recipe_name: string;
  recipe_type: string;
  total_servings_remaining: number;
  oldest_batch_date: string;
  newest_batch_date: string;
  batch_count: number;
  batches: BatchStock[];
}

export interface Ingredient {
  id: string;
  name: string;
  type: string | null;
  staple: number;
  density_g_per_ml: number | null;
  preferred_unit: string | null;
}

export interface WeekSlot {
  id: string;
  day_of_week: string;
  meal_type: "lunch" | "dinner";
  note: string | null;
  recipes: {
    slot_id: string;
    recipe_id: string;
    servings: number;
    recipe_name: string;
    recipe_type: string;
  }[];
}

export interface Week {
  id: string;
  start_date: string;
  notes: string | null;
  slots: WeekSlot[];
}

export interface SessionBatch {
  id: string;
  recipe_id: string;
  recipe_name: string;
  recipe_type: string;
  session_id: string;
  servings_produced: number;
  servings_consumed: number;
  servings_remaining: number;
  prep_date: string;
  storage_location: "fridge" | "freezer";
  variant_id: string | null;
  variant_name: string | null;
}

export interface Session {
  id: string;
  date: string;
  notes: string | null;
  gantt: string | null;
  status: "planned" | "completed";
  batch_count: number;
  total_servings: number;
  batches?: SessionBatch[];
}

export interface GroceryRepresentation {
  quantity: number;
  quantity_max?: number;
  unit: string;
  optional_quantity?: number;
  optional_quantity_max?: number;
}

export interface GroceryItem {
  ingredient_id: string;
  name: string;
  type: string;
  descriptive: string[];
  recipes: string[];
  representations: GroceryRepresentation[];
}

export type GroceryList = {
  phase: string;
  items: Record<string, GroceryItem[]>;
};

export interface GrocerySelectionInput {
  recipe_id: string;
  phase: "meal-prep" | "day-of-eating" | "both";
  servings: number;
  variant_id?: string | null;
}

// ─── Recipes ──────────────────────────────────────────────────────────────────

export const recipesApi = {
  list: () => get<Recipe[]>("/api/recipes"),
  get: (id: string) => get<RecipeDetail>(`/api/recipes/${id}`),
  create: (data: Partial<Recipe>) => post<{ id: string }>("/api/recipes", data),
  update: (id: string, data: Partial<Recipe>) => put<{ id: string }>(`/api/recipes/${id}`, data),
  updateSteps: (id: string, steps: Partial<Step>[]) =>
    put<{ updated: boolean }>(`/api/recipes/${id}/steps`, steps),
  delete: (id: string) => del<{ deleted: boolean }>(`/api/recipes/${id}`),
  addVariant: (recipeId: string, name: string) =>
    post<Variant>(`/api/recipes/${recipeId}/variants`, { name }),
  removeVariant: (recipeId: string, variantId: string) =>
    del<{ deleted: boolean }>(`/api/recipes/${recipeId}/variants/${variantId}`),
};

// ─── Resources ───────────────────────────────────────────────────────────────

export interface Resource {
  id: string;
  name: string;
  type: string;
}

export const resourcesApi = {
  list: () => get<Resource[]>("/api/resources"),
  create: (data: { name: string; type?: string }) => post<Resource>("/api/resources", data),
};

// ─── Ingredients ──────────────────────────────────────────────────────────────

export interface IngredientRecipe {
  id: string;
  name: string;
  type: string | null;
  servings: number | null;
  quantity: string | null;
  unit: string | null;
  phase: string | null;
}

export interface IngredientConversion {
  from_unit: string;
  to_unit: "g" | "ml";
  factor_min: number;
  factor_max: number;
}

export const ingredientsApi = {
  list: () => get<Ingredient[]>("/api/ingredients"),
  recipes: (ingredientId: string) =>
    get<IngredientRecipe[]>(`/api/ingredients/${ingredientId}/recipes`),
  conversions: (ingredientId: string) =>
    get<IngredientConversion[]>(`/api/ingredients/${ingredientId}/conversions`),
  conversionsBulk: (ids: string[]) =>
    get<Record<string, IngredientConversion[]>>(
      `/api/ingredients/conversions/bulk?ids=${ids.join(",")}`,
    ),
  updateConversions: (
    ingredientId: string,
    conversions: { from_unit: string; to_unit: string; factor_min: number; factor_max: number }[],
  ) => put<IngredientConversion[]>(`/api/ingredients/${ingredientId}/conversions`, conversions),
  create: (data: { name: string; type?: string; density_g_per_ml?: number | null }) =>
    post<Ingredient>("/api/ingredients", data),
  update: (
    id: string,
    data: {
      name?: string;
      type?: string | null;
      staple?: boolean;
      density_g_per_ml?: number | null;
      preferred_unit?: string | null;
    },
  ) => patch<Ingredient>(`/api/ingredients/${id}`, data),
  delete: (id: string) => del<{ deleted: boolean }>(`/api/ingredients/${id}`),
};

// ─── Stock ────────────────────────────────────────────────────────────────────

export interface Consumption {
  id: string;
  batch_id: string;
  servings_consumed: number;
  consumption_date: string;
  recipe_id: string;
  recipe_name: string;
}

export const stockApi = {
  list: () => get<RecipeStock[]>("/api/stock"),
  expiring: (days = 3) => get<BatchStock[]>(`/api/stock/expiring?days=${days}`),
  consumptions: (from: string, to: string) =>
    get<Consumption[]>(`/api/stock/consumptions?from=${from}&to=${to}`),
  batchConsumptions: (batchId: string) =>
    get<{ id: string; servings_consumed: number; consumption_date: string }[]>(
      `/api/stock/consumptions/${batchId}`,
    ),
  consume: (data: {
    batch_id?: string;
    recipe_id?: string;
    servings_consumed: number;
    consumption_date?: string;
  }) => post<{ id?: string; ids?: string[] }>("/api/stock/consume", data),
  updateConsumption: (
    id: string,
    data: { servings_consumed?: number; consumption_date?: string },
  ) => patch<{ id: string }>(`/api/stock/consumptions/${id}`, data),
  deleteConsumption: (id: string) => del<{ deleted: boolean }>(`/api/stock/consumptions/${id}`),
};

// ─── Weeks ────────────────────────────────────────────────────────────────────

export const weeksApi = {
  list: () => get<Week[]>("/api/weeks"),
  current: () => get<Week>("/api/weeks/current"),
  create: (startDate: string) => post<Week>("/api/weeks", { start_date: startDate }),
  get: (id: string) => get<Week>(`/api/weeks/${id}`),
  updateNotes: (id: string, notes: string | null) =>
    put<{ id: string }>(`/api/weeks/${id}`, { notes }),
  setSlots: (
    id: string,
    slots: {
      day_of_week: string;
      meal_type: string;
      note?: string | null;
      recipes: { recipe_id: string; servings: number }[];
    }[],
  ) => put<{ updated: boolean }>(`/api/weeks/${id}/slots`, slots),
  deleteSlot: (weekId: string, slotId: string) =>
    del<{ deleted: boolean }>(`/api/weeks/${weekId}/slots/${slotId}`),
  copyWeek: (weekId: string, targetStartDate: string) =>
    post<{ copied: boolean; target_week_id: string }>(`/api/weeks/${weekId}/copy`, {
      target_start_date: targetStartDate,
    }),
  groceryList: (id: string) =>
    get<{ days: { day: string; items: Record<string, GroceryItem[]> }[] }>(
      `/api/weeks/${id}/grocery-list`,
    ),
  groceryChecks: (id: string) => get<string[]>(`/api/weeks/${id}/grocery-checks`),
  setGroceryCheck: (id: string, itemKey: string, checked: boolean) =>
    put<{ changed: boolean }>(`/api/weeks/${id}/grocery-checks`, { item_key: itemKey, checked }),
};

// ─── Standalone grocery list ────────────────────────────────────────────────

export const groceryApi = {
  fromSelections: (selections: GrocerySelectionInput[]) =>
    post<{ items: Record<string, GroceryItem[]> }>("/api/grocery-list", { selections }),
};

// ─── Sessions ─────────────────────────────────────────────────────────────────

export const sessionsApi = {
  list: () => get<Session[]>("/api/sessions"),
  get: (id: string) => get<Session>(`/api/sessions/${id}`),
  create: (data: { date: string; notes?: string; gantt?: string; status?: string }) =>
    post<{ id: string }>("/api/sessions", data),
  update: (id: string, data: Partial<Pick<Session, "date" | "notes" | "gantt" | "status">>) =>
    put<{ id: string }>(`/api/sessions/${id}`, data),
  delete: (id: string) => del<{ deleted: boolean }>(`/api/sessions/${id}`),
  addBatch: (
    sessionId: string,
    data: {
      recipe_id: string;
      servings_produced: number;
      prep_date: string;
      storage_location?: string;
      variant_id?: string | null;
    },
  ) => post<{ id: string }>(`/api/sessions/${sessionId}/batches`, data),
  updateBatch: (
    sessionId: string,
    batchId: string,
    data: { storage_location?: string; servings_produced?: number; variant_id?: string | null },
  ) =>
    request<{ id: string }>(`/api/sessions/${sessionId}/batches/${batchId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteBatch: (sessionId: string, batchId: string) =>
    del<{ deleted: boolean }>(`/api/sessions/${sessionId}/batches/${batchId}`),
  groceryList: (sessionId: string, phase: "meal-prep" | "day-of-eating" | "all" = "meal-prep") =>
    get<GroceryList>(`/api/sessions/${sessionId}/grocery-list?phase=${phase}`),
  groceryChecks: (id: string) => get<string[]>(`/api/sessions/${id}/grocery-checks`),
  setGroceryCheck: (id: string, itemKey: string, checked: boolean) =>
    put<{ changed: boolean }>(`/api/sessions/${id}/grocery-checks`, {
      item_key: itemKey,
      checked,
    }),
  cookbooks: (sessionId: string) =>
    get<{ cookbooks: string[] }>(`/api/sessions/${sessionId}/cookbooks`),
};
